import {
  Button,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { Autocomplete } from '@mui/material';
import React, { useState } from "react";
import { InventoryPartUsed } from "../../api/api-generated";
import {
  useFindAllInventoryPartsQuery,
  useFindAllInventoryPartsUsedQuery,
  useRemoveInventoryPartUsedMutation,
  useUpdateInventoryPartMutation,
  useUpdateInventoryPartUsedMutation,
} from "../../api/api-hooks";

interface Props {
  removeFormActive: boolean;
  setRemoveFormActive: React.Dispatch<React.SetStateAction<boolean>>;
  partsUsed: InventoryPartUsed[];
}

interface Form {
  part: InventoryPartUsed | null;
  amount: number;
}

const initialFormState: Form = {
  part: null,
  amount: 0,
};

export default function LptRMAPartRemover({
  removeFormActive,
  setRemoveFormActive,
  partsUsed,
}: Props) {
  const { data: availableParts } = useFindAllInventoryPartsQuery({});
  const { data: usedParts } = useFindAllInventoryPartsUsedQuery({});
  const [formState, setFormState] = useState<Form>(initialFormState);
  const [partsUsedUpdater] = useUpdateInventoryPartUsedMutation();
  const [partsUsedDeleter] = useRemoveInventoryPartUsedMutation();
  const [partUpdater] = useUpdateInventoryPartMutation();
  const [feedback, setFeedback] = useState<string>("");

  if (availableParts === undefined || usedParts === undefined) return null;
  if (!removeFormActive) return null;

  const handleChange = (event: any, partValue?: InventoryPartUsed | null) => {
    const { name, value } = event.target;
    if (partValue) {
      setFormState({ ...formState, part: partValue });
    } else {
      if (
        name === "amount" &&
        (!formState.part || value > formState.part.amount || value < 1)
      )
        return;
      setFormState({ ...formState, [name]: value });
    }
  };
  const removePart = () => {
    if (!formState.part) return;
    if (formState.part.amount - formState.amount === 0) {
      partsUsedDeleter({
        id: formState.part.id,
      });
    } else {
      partsUsedUpdater({
        id: formState.part.id,
        updateInventoryPartsUsedDto: {
          id: formState.part.id,
          amount: Number(formState.part.amount) - formState.amount,
        },
      });
    }
    setRemoveFormActive(false);
    setFormState(initialFormState);
  };
  const removePartAndRestore = () => {
    if (!formState.part) return;
    if (formState.part.amount - formState.amount === 0) {
      partsUsedDeleter({
        id: formState.part.id,
      });
    } else {
      partsUsedUpdater({
        id: formState.part.id,
        updateInventoryPartsUsedDto: {
          id: formState.part.id,
          amount: Number(formState.part.amount) - formState.amount,
        },
      });
    }
    // Find corresponding part ID from inventory parts
    const inventoryPart = availableParts.find(
      (part) => part.partName === formState?.part?.partName
    );
    if (!inventoryPart) {
      setFeedback(
        "No inventory part corresponding to the part used. No parts have been restored to inventory."
      );
      return;
    }
    // Restore deleted parts to inventory
    partUpdater({
      id: inventoryPart.id,
      updateInventoryPartDto: {
        id: inventoryPart.id,
        amount: Number(inventoryPart.amount) + Number(formState.amount),
      },
    });
    setRemoveFormActive(false);
    setFormState(initialFormState);
  };
  const handleCancel = () => {
    setRemoveFormActive(false);
  };

  return (
    <Grid container direction="column" spacing={2}>
      <Divider style={{ marginBottom: "1rem" }} />
      <Grid item>
        <Typography variant="h6">Remove Parts</Typography>
      </Grid>
      <Grid item>
        <Autocomplete
          onChange={handleChange}
          value={formState.part}
          options={partsUsed}
          getOptionLabel={(part) => part.partName}
          style={{ width: 300 }}
          renderInput={(params) => (
            <TextField {...params} label="Select Part" variant="outlined" />
          )}
        />
      </Grid>
      {formState.part && (
        <>
          <Grid item>
            <TextField
              value={formState.amount}
              label="Amount"
              type="number"
              name="amount"
              helperText={`Amount Used: ${formState.part.amount}`}
              onChange={handleChange}
            ></TextField>
          </Grid>
        </>
      )}
      {formState.part && formState.amount < 1 && (
        <Grid item>
          <Typography color="error">Amount must be greater than 0.</Typography>
        </Grid>
      )}
      <Grid item container spacing={1}>
        <>
          <Grid item>
            <Button
              variant="contained"
              disabled={!formState.part || formState.amount === 0}
              onClick={removePart}
            >
              Remove
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              disabled={!formState.part || formState.amount === 0}
              onClick={removePartAndRestore}
            >
              Remove and Return to Inventory
            </Button>
          </Grid>
        </>
        <Grid item>
          <Button variant="outlined" onClick={handleCancel}>
            Cancel
          </Button>
        </Grid>
        {feedback && (
          <Grid item>
            <Typography>{feedback}</Typography>
          </Grid>
        )}
      </Grid>

      <Divider style={{ marginTop: "1rem" }} />
    </Grid>
  );
}
